import { money, multiplyMoney, type Money } from "./money.js";

/**
 * The one source of pricing facts (IMPLEMENTATION_PLAN.md §2.8), confirmed by the owner on
 * 2026-09-09 against the public pricing page: five plans priced by active-campaign capacity,
 * with AI work metered in credits. Stripe holds one Price per paid plan (their ids come from
 * STRIPE_PRICE_*); the amounts here must agree with them and are what the UI shows before
 * checkout. Changing a number here is the whole change — nothing else in the codebase
 * knows a price.
 */

export const PLAN_CODES = ["free", "starter", "growth", "scale", "scale_50"] as const;
export type PlanCode = (typeof PLAN_CODES)[number];

/** Plans with a Stripe Price; the free plan is what an organization has without one. */
export const PAID_PLAN_CODES = ["starter", "growth", "scale", "scale_50"] as const;
export type PaidPlanCode = (typeof PAID_PLAN_CODES)[number];

export const PRICING_CURRENCY = "USD";

/** The free plan runs the full autopilot for this long after the organization is created. */
export const FREE_TRIAL_DAYS = 14;

/**
 * A beta link's code gives the plan free for this long with no card; Stripe then charges
 * it if the user has added a card, and otherwise cancels it back to the free plan.
 */
export const BETA_TRIAL_DAYS = 30;

/** Video ads are hidden during the beta (owner, 19.10.2026); the pipeline stays in place. */
export const VIDEO_ADS_ENABLED = false as boolean;

/**
 * A Stripe promotion code carried in a beta link (`/register?code=BETA2026`). Anything that
 * does not look like a code becomes null, so a mangled link never blocks registration; the
 * code is checked against Stripe only at checkout.
 */
export function normalizePromotionCode(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const code = value.trim();
  return /^[A-Za-z0-9_-]{1,64}$/.test(code) ? code : null;
}

/** Additional manual creative work per Starter monthly billing period; initial assets are separate. */
export const STARTER_MANUAL_CREATIVE_ALLOWANCE = { images: 20, text_batches: 15 } as const;

export interface PlanCredits {
  readonly amount: number;
  /** `monthly`: granted on every paid invoice. `once`: granted when the organization is created. */
  readonly cadence: "monthly" | "once";
}

export interface Plan {
  readonly code: PlanCode;
  /** Display name, the same in every language. */
  readonly name: string;
  /** List price per month; zero for the free plan. */
  readonly monthlyPrice: Money;
  readonly credits: PlanCredits;
  /** Campaign groups that may be active at once. */
  readonly activeCampaignLimit: number;
  /** Distinct headline/message concepts generated per campaign (Google RSA caps at 15). */
  readonly creativeConcepts: number;
  readonly adImages: number;
  readonly videos: number;
  /** Whether consumption may exceed the allowance; never today — top-ups are bought, not charged. */
  readonly overageAllowed: boolean;
  /** Price per credit beyond the allowance (zero while overage is not allowed). */
  readonly overageUnitPrice: Money;
  /** Stripe trial at checkout; none today — the free plan's autopilot trial is FREE_TRIAL_DAYS. */
  readonly trialDays: number;
}

const NO_OVERAGE = money(0, PRICING_CURRENCY);

export const PLANS: Readonly<Record<PlanCode, Plan>> = {
  free: {
    code: "free",
    name: "Free",
    monthlyPrice: money(0, PRICING_CURRENCY),
    credits: { amount: 60, cadence: "once" },
    activeCampaignLimit: 1,
    creativeConcepts: 4,
    // Four, so even a free campaign tests one image of each line (SP-040).
    adImages: 4,
    videos: 1,
    overageAllowed: false,
    overageUnitPrice: NO_OVERAGE,
    trialDays: 0,
  },
  starter: {
    code: "starter",
    name: "Starter",
    monthlyPrice: money(2_900, PRICING_CURRENCY),
    credits: { amount: 300, cadence: "monthly" },
    activeCampaignLimit: 2,
    creativeConcepts: 6,
    adImages: 5,
    videos: 1,
    overageAllowed: false,
    overageUnitPrice: NO_OVERAGE,
    trialDays: 0,
  },
  growth: {
    code: "growth",
    name: "Growth",
    monthlyPrice: money(7_900, PRICING_CURRENCY),
    credits: { amount: 600, cadence: "monthly" },
    activeCampaignLimit: 10,
    creativeConcepts: 10,
    adImages: 7,
    videos: 2,
    overageAllowed: false,
    overageUnitPrice: NO_OVERAGE,
    trialDays: 0,
  },
  scale: {
    code: "scale",
    name: "Scale 25",
    monthlyPrice: money(49_900, PRICING_CURRENCY),
    credits: { amount: 1_200, cadence: "monthly" },
    activeCampaignLimit: 25,
    creativeConcepts: 15,
    adImages: 10,
    videos: 3,
    overageAllowed: false,
    overageUnitPrice: NO_OVERAGE,
    trialDays: 0,
  },
  scale_50: {
    code: "scale_50",
    name: "Scale 50",
    monthlyPrice: money(99_900, PRICING_CURRENCY),
    credits: { amount: 1_200, cadence: "monthly" },
    activeCampaignLimit: 50,
    creativeConcepts: 15,
    adImages: 10,
    videos: 3,
    overageAllowed: false,
    overageUnitPrice: NO_OVERAGE,
    trialDays: 0,
  },
};

export const PLAN_LIST: readonly Plan[] = PLAN_CODES.map((code) => PLANS[code]);

/**
 * Plans on sale. Scale 25 and Scale 50 are hidden for the beta (owner, 4.11.2026): they are
 * left off the pricing page and the billing settings, and checkout refuses them. Put them
 * back here, with live Stripe prices, when they go on sale.
 */
export const OFFERED_PLAN_CODES: readonly PlanCode[] = ["free", "starter", "growth"];

export const OFFERED_PLAN_LIST: readonly Plan[] = OFFERED_PLAN_CODES.map((code) => PLANS[code]);

export function isOfferedPlanCode(code: PlanCode): boolean {
  return OFFERED_PLAN_CODES.includes(code);
}

export function isPlanCode(value: unknown): value is PlanCode {
  return typeof value === "string" && (PLAN_CODES as readonly string[]).includes(value);
}

export function isPaidPlanCode(value: unknown): value is PaidPlanCode {
  return typeof value === "string" && (PAID_PLAN_CODES as readonly string[]).includes(value);
}

export function planByCode(code: string): Plan | undefined {
  return isPlanCode(code) ? PLANS[code] : undefined;
}

/** Credits consumed beyond the allowance in a period; never negative. */
export function overageCredits(plan: Plan, consumedCredits: number): number {
  if (!Number.isInteger(consumedCredits) || consumedCredits < 0) {
    throw new RangeError(`consumedCredits must be a non-negative integer, got ${consumedCredits}`);
  }
  return Math.max(0, consumedCredits - plan.credits.amount);
}

/** What `credits` of overage cost on the plan (zero when overage is not allowed). */
export function overageCharge(plan: Plan, credits: number): Money {
  if (!Number.isInteger(credits) || credits < 0) {
    throw new RangeError(`credits must be a non-negative integer, got ${credits}`);
  }
  if (!plan.overageAllowed) return money(0, plan.overageUnitPrice.currency);
  return multiplyMoney(plan.overageUnitPrice, credits);
}
